import { isAxiosError } from 'axios'
import { getUserFacingErrorMessage, sanitizeBusinessMessage } from '../utils/userFacingError'
import { api } from './api'

/**
 * `POST /uploads/images` — multipart field `files` (repeat field for multiple).
 *
 * Uses Axios with `FormData` and `onUploadProgress` so the UI can show a determinate
 * progress bar. Per-request headers override the default JSON content-type.
 */
export async function uploadProductImages(
  fileList: FileList | File[] | null,
  onProgress?: (percent: number) => void,
  options?: { purpose?: 'logo' | 'favicon' },
): Promise<string[]> {
  const list: File[] = fileList ? Array.from(fileList as ArrayLike<File>) : []
  if (list.length === 0) {
    throw new Error('Select at least one image')
  }

  const formData = new FormData()
  for (const f of list) {
    formData.append('files', f)
  }
  const totalBytes = list.reduce((s, f) => s + f.size, 0)

  try {
    const { data } = await api.post('/uploads/images', formData, {
      params: options?.purpose ? { purpose: options.purpose } : undefined,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (evt) => {
        if (!onProgress) return
        const total = evt.total || totalBytes || 0
        if (!total) return
        onProgress(Math.min(100, Math.round((evt.loaded / total) * 100)))
      },
    })

    const body = data as {
      success?: boolean
      message?: string
      error?: { code?: string }
      data?: { items?: Array<{ url?: string }> }
    }

    if (!body?.success) {
      const code = body?.error?.code
      const msg = sanitizeBusinessMessage(body?.message, 'Upload failed')
      if (code === 'INVALID_TOKEN' || code === 'AUTH_REQUIRED') {
        throw new Error(
          `${msg} Sign out and log in again if you have been idle — access tokens expire quickly by default (often 15 minutes).`,
        )
      }
      throw new Error(msg)
    }

    const items = body.data?.items
    if (!Array.isArray(items)) {
      throw new Error('Invalid upload response')
    }
    return items.map((item) => item.url).filter(Boolean) as string[]
  } catch (err: unknown) {
    if (isAxiosError(err) && err.response?.data) {
      const d = err.response.data as { message?: string; error?: { code?: string } }
      const code = d?.error?.code
      if (code === 'INVALID_TOKEN' || code === 'AUTH_REQUIRED') {
        const msg = sanitizeBusinessMessage(d?.message, 'Your session has expired.')
        throw new Error(
          `${msg} Sign out and log in again if you have been idle — access tokens expire quickly by default (often 15 minutes).`,
        )
      }
    }
    throw new Error(getUserFacingErrorMessage(err, 'Upload failed'))
  }
}
